import React from "react";
import { Alert, Button, Card, Space, Typography } from "antd";
import SolicitacaoReservaStatusTag, {
  type StatusSolicitacaoReservaFront,
} from "../components/SolicitacaoReservaStatusTag";
import type {
  PagamentoTaxaCheckoutStatus,
  ResumoEstadiaByTokenResponse,
} from "../../../services/solicitacaoReservaService";

const { Title, Text, Paragraph } = Typography;

const moeda = (v: number | string) =>
  Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

type CheckoutReturnStatus = "success" | "cancel" | "expired" | null;

type Props = {
  resumo: ResumoEstadiaByTokenResponse | null;
  statusSolicitacao: StatusSolicitacaoReservaFront | null;
  loadingPagamento?: boolean;
  pagamentoStatus?: PagamentoTaxaCheckoutStatus | null;
  checkoutRetornoStatus?: CheckoutReturnStatus;
  checkoutDisponivel?: boolean;
  onIrParaPagamento: () => void;
};

const pagamentoStatusConfig: Record<
  PagamentoTaxaCheckoutStatus,
  { type: "success" | "info" | "warning" | "error"; message: string; description: string }
> = {
  PENDENTE: {
    type: "warning",
    message: "Checkout aguardando pagamento",
    description:
      "Seu checkout já foi gerado. Ao abrir, você poderá escolher Pix ou cartão para concluir a taxa.",
  },
  RECEBIDO: {
    type: "success",
    message: "Pagamento confirmado",
    description:
      "Recebemos a taxa de verificação. Se a tela ainda não avançou, a atualização pode levar alguns instantes.",
  },
  VENCIDO: {
    type: "error",
    message: "Checkout vencido",
    description: "O prazo desse checkout expirou. Gere um novo checkout para continuar.",
  },
  CANCELADO: {
    type: "info",
    message: "Checkout cancelado",
    description: "Você pode gerar um novo checkout quando quiser continuar com a taxa.",
  },
};

const checkoutReturnConfig: Record<
  Exclude<CheckoutReturnStatus, null>,
  { type: "success" | "info" | "warning" | "error"; message: string; description: string }
> = {
  success: {
    type: "info",
    message: "Retorno do checkout recebido",
    description: "Estamos consultando a confirmação mais recente do pagamento da taxa.",
  },
  cancel: {
    type: "warning",
    message: "Checkout cancelado",
    description: "Você pode reabrir o checkout e escolher Pix ou cartão novamente.",
  },
  expired: {
    type: "error",
    message: "Checkout expirado",
    description: "Gere um novo checkout para continuar com o pagamento da taxa.",
  },
};

export default function StepAguardandoPagamentoTaxa({
  resumo,
  statusSolicitacao,
  loadingPagamento = false,
  pagamentoStatus = null,
  checkoutRetornoStatus = null,
  checkoutDisponivel = false,
  onIrParaPagamento,
}: Props) {
  const valorTaxa = resumo?.taxaCondicional ?? 0;
  const paymentAlert = pagamentoStatus ? pagamentoStatusConfig[pagamentoStatus] : null;
  const returnAlert =
    checkoutRetornoStatus && !(checkoutRetornoStatus === "success" && pagamentoStatus === "RECEBIDO")
      ? checkoutReturnConfig[checkoutRetornoStatus]
      : null;

  const shouldShowAction = pagamentoStatus !== "RECEBIDO";
  const buttonLabel =
    pagamentoStatus === "VENCIDO" || pagamentoStatus === "CANCELADO"
      ? "Gerar novo checkout"
      : checkoutDisponivel || pagamentoStatus === "PENDENTE"
        ? "Pagar taxa"
        : "Abrir checkout da taxa";

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Card
        bordered={false}
        style={{ borderRadius: 16, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}
        styles={{ body: { padding: 24 } }}
      >
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <div>
            <SolicitacaoReservaStatusTag status={statusSolicitacao} />
          </div>

          <div>
            <Title level={2} style={{ marginBottom: 8 }}>
              Aguardando pagamento da taxa
            </Title>
            <Paragraph style={{ marginBottom: 0 }}>
              Sua solicitação foi registrada. Para iniciarmos a verificação de
              disponibilidade, é necessário concluir o pagamento da taxa.
            </Paragraph>
          </div>

          {returnAlert && (
            <Alert
              type={returnAlert.type}
              showIcon
              message={returnAlert.message}
              description={returnAlert.description}
              style={{ borderRadius: 14 }}
            />
          )}

          {paymentAlert ? (
            <Alert
              type={paymentAlert.type}
              showIcon
              message={paymentAlert.message}
              description={paymentAlert.description}
              style={{ borderRadius: 14 }}
            />
          ) : (
            <Alert
              type="warning"
              showIcon
              message="Pagamento pendente"
              description="Ao abrir o checkout, você poderá escolher Pix ou cartão para pagar a taxa."
              style={{ borderRadius: 14 }}
            />
          )}

          <Card size="small" style={{ borderRadius: 14 }}>
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Text>
                <strong>Taxa de verificação:</strong> {moeda(valorTaxa)}
              </Text>
              <Text>
                <strong>Métodos disponíveis no checkout:</strong> Pix ou cartão
              </Text>
              <Text type="secondary">
                O pagamento da taxa abre o checkout único, sem necessidade de escolher o
                método antes.
              </Text>
            </Space>
          </Card>

          {shouldShowAction && (
            <Button
              type="primary"
              size="large"
              block
              loading={loadingPagamento}
              onClick={onIrParaPagamento}
            >
              {buttonLabel}
            </Button>
          )}

          <Text type="secondary" style={{ fontSize: 13 }}>
            No próximo passo você poderá escolher Pix ou cartão. Se você saiu da tela do
            checkout, pode retomá-la pelo botão acima.
          </Text>
        </Space>
      </Card>
    </div>
  );
}
